
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import EditFrame from "@/components/EditFrame.vue";
import { Manager } from "@/api/dtos/Manager.schema";
import DangerButton from "../buttons/DangerButton.vue";
import AddButton from "../buttons/AddButton.vue";
import CancelButton from "../buttons/CancelButton.vue";
import AddPersonDialog from "@/components/dialogs/AddPersonDialog.vue";
import DangerDialog from "@/components/dialogs/DangerDialog.vue";
import moment from "moment";
import api from "@/api";
import { StationSummary } from "@/api/dtos/StationSummary.schema";
import { PendingInvitation,Operator } from "@/api/dtos/Operator.schema";
import { OperatorsModule } from "@/store/modules/OperatorsModule";
import { deepClone } from "@/misc/helpers";
import { StationsModule } from "@/store/modules/StationsModule";
import { showToast } from "@/utils/toastService";
import { getAxiosResponse } from "@/utils/errorUtils";

type EditableManager = Manager & {
  selectedStations: StationSummary[];
  selectedStationIds: string[];
};

type ManagerRow =
  | (EditableManager & { kind: "ACTIVE" })
  | {
      kind: "PENDING";
      email: string;
      status: "PENDING" | "EXPIRED";
      selectedStations: StationSummary[];
      selectedStationIds: string[];
      userSummary: { userUuid: string; email: string; lastLogin: string | null };
    };

@Component({
  components: {
    EditFrame,
    CancelButton,
    DangerButton,
    AddButton,
    AddPersonDialog,
    DangerDialog,
  },
})
export default class Managers extends Vue {
  moment = moment;

  readonly headers = [
    { text: "Email", value: "user.email", align: "center" },
    { text: "Status / Letzter Login", value: "user.lastLogin", align: "center" },
    { text: "Manager für", value: "selectedStations", align: "center" },
    { text: "", value: "actions", align: "center" },
  ];

  @Prop()
  readonly operator!: Operator;

  editedManagers: EditableManager[] = [];
  dialog = false;
  showRemoveManagerDialog = false;
  managerToRemove: EditableManager | null = null;
  cachedStations: StationSummary[] = [];

  async mounted(): Promise<void> {
    StationsModule.CLEAR_OPERATOR_STATIONS();
    await StationsModule.fetchStationSummariesForOperator(this.operator.uuid);
    // await StationsModule.fetchStationSummaries();
    this.rehydrateSelections();
  }

  get stations(): StationSummary[] {
    return StationsModule.operatorStations;
  }

  get tableManagers(): ManagerRow[] {
    const active = (this.editedManagers ?? []).map(m => ({ ...m, kind: "ACTIVE" as const }));

    const pending = (this.operator.pendingManagers ?? []).map((p: PendingInvitation) => ({
      kind: "PENDING" as const,
      email: p.email,
      status: p.status,
      selectedStations: [],
      selectedStationIds: [],
      userSummary: {
        userUuid: "",
        email: p.email,
        lastLogin: null,
      },
    }));

    console.log("PENDEHGO", pending)

    return [...active, ...pending];
  }

  async onAddManager(email: string): Promise<void> {
    try {
      await api.OperatorsApi.addManager(this.operator.uuid, email);

      showToast({
        message: `Manager erfolgreich hinzugefügt`,
        type: "success",
      });

      OperatorsModule.refreshDetailedOperator();
    } catch (e) {
      showToast({
        response: getAxiosResponse(e),
        message: `Fehler beim Hinzufügen des Managers`,
        type: "error",
      });
      console.error("Failed to add manager:", e);
    }
  }

  async onRemoveManager(): Promise<void> {
    try {
      if (this.managerToRemove === null) return;
      await api.OperatorsApi.deleteManager(
        this.operator.uuid,
        this.managerToRemove.userSummary.userUuid
      );

      showToast({
        message: `Manager erfolgreich entfernt`,
        type: "success",
      });

      OperatorsModule.refreshDetailedOperator();
      this.managerToRemove = null;
    } catch (e) {
      showToast({
        response: getAxiosResponse(e),
        message: `Fehler beim Entfernen des Managers`,
        type: "error",
      });
      console.error("Failed to remove manager:", e);
    }
  }

  @Watch("operator", { immediate: true, deep: true })
  backupData(): void {
    const managers = deepClone(this.operator.managers) as Manager[];
    this.editedManagers = managers.map((m) => ({
      ...m,
      selectedStationIds: (m.stations || []).map((s) => s.stationUuid),
      selectedStations: [],
    }));
    console.log(this.editedManagers);
    this.rehydrateSelections();
  }

  @Watch("stations", { immediate: true, deep: true })
  onStationsChanged(): void {
    this.rehydrateSelections();
  }

  private rehydrateSelections(): void {
    if (!this.stations || this.stations.length === 0 || !this.editedManagers)
      return;
    const byId = new Map(this.stations.map((s) => [s.uuid, s] as const));
    this.editedManagers.forEach((m) => {
      m.selectedStations = (m.selectedStationIds || [])
        .map((id) => byId.get(id))
        .filter((x): x is StationSummary => !!x);
    });
  }

  onRemoveManagerClick(manager: any): void {
    if (manager.kind === "PENDING") return;
    this.showRemoveManagerDialog = true;
    this.managerToRemove = manager;
  }

  cacheData(isFocused: boolean,manager:EditableManager): void {
    this.cachedStations = manager.selectedStations;
  }

  onMenuChange(isOpen: boolean, manager: EditableManager) {
    const sameStations = this.stationsEqual(this.cachedStations, manager.selectedStations);

    if (!sameStations) {
      // Menu closed → user finished selecting for THIS manager/row
      console.log('Final selection for', manager.userSummary.email, manager.selectedStations);
      this.onManagerStationsChanged(manager);
    }
  }

  private stationsEqual(a: StationSummary[], b: StationSummary[]): boolean {
    if (a.length !== b.length) return false;
    const aIds = a.map(s => s.uuid).sort();
    const bIds = b.map(s => s.uuid).sort();
    return aIds.every((id, index) => id === bIds[index]);
  }

  onManagerStationsChanged(manager: EditableManager): void {
    this.$nextTick(() => {
      const payload: StationSummary[] = Array.isArray(manager.selectedStations)
        ? manager.selectedStations
        : [];

      manager.selectedStationIds = payload.map((s) => s.uuid);

      api.OperatorsApi.setManagerStations(
        this.operator.uuid,
        manager.userSummary.userUuid,
        manager.selectedStationIds
      );
    });
  }

  isDeleteDisabled(item: any): boolean {
    if (item.kind === "PENDING") return true;
    return (item.selectedStations?.length || 0) > 0;
  }

  deleteTooltip(item: EditableManager): string {
    return this.isDeleteDisabled(item)
      ? "Kann nicht gelöscht werden: Station(en) zugewiesen"
      : "Löschen";
  }

  async onResendManagerInvite(email: string): Promise<void> {
    try {
      await api.OperatorsApi.resendManagerInvite(this.operator.uuid, email);
      showToast({ message: "Einladung erneut gesendet", type: "success" });
      OperatorsModule.refreshDetailedOperator();
    } catch (e) {
      showToast({
        response: getAxiosResponse(e),
        message: "Fehler beim erneuten Senden",
        type: "error",
      });
    }
  }
}
